import React, { useEffect, useRef, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Product, Machine, ProductionPhaseType, ProductionUnit } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import PhaseEditor, { PhaseRow, phasesToRows, rowsToPayload } from "../../../components/MES/PhaseEditor";
import PhaseMaterialEditor, { PhaseMaterialEditorHandle } from "../../../components/MES/PhaseMaterialEditor";
import { t } from "../../../i18n/locale";

/**
 * Trajanje faze za prikaz u listi. Editor faza sprema trajanja u ProductionPhase
 * retke i ne dira stare stupce na proizvodu (MixingTimeMin i sl.), pa se oni
 * koriste samo za proizvode koji jos nemaju definirane faze.
 */
const phaseDuration = (product: Product, code: string, legacy?: number | null) => {
  const phases = product.ProductionPhase;
  const value = phases?.length
    ? phases.find((p) => p.ProductionPhaseType_ProductionPhaseToPhaseType?.Code === code)?.DurationMin
    : legacy;
  return value == null ? "-" : t("{value} min", { value: Number(value) });
};

/**
 * Products Database Page
 * Manage product master data, machines, and process times
 */
const Products = () => {
  savePathToLocalStorage();

  const [products, setProducts] = useState<Product[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [productionUnits, setProductionUnits] = useState<ProductionUnit[]>([]);
  const [phaseTypes, setPhaseTypes] = useState<ProductionPhaseType[]>([]);
  const [phaseRows, setPhaseRows] = useState<PhaseRow[]>([]);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const phaseMaterialRef = useRef<PhaseMaterialEditorHandle>(null);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [unitFilter, setUnitFilter] = useState<string>("");

  // Bulk edit: select multiple products and assign machines to all at once.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkMixer, setBulkMixer] = useState<string>("");
  const [bulkProcessing, setBulkProcessing] = useState<string>("");
  const [bulkOven, setBulkOven] = useState<string>("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const emptyProduct: Product = {
    ID: 0,
    ProductName: "",
    ProductCode: "",
    Category: "",
    IsActive: true,
    HideInTraceability: false,
  };

  // Machine types are stored in Croatian in the DB (Mikser/Obrada/Peć); accept the
  // legacy English aliases too so filtering works regardless of the stored value.
  const MACHINE_TYPE_ALIASES: Record<string, string[]> = {
    "Mikser": ["Mikser", "mixer"],
    "Obrada": ["Obrada", "processor"],
    "Peć": ["Peć", "Pec", "oven"],
  };
  const isMachineType = (m: Machine, canonical: string) =>
    (MACHINE_TYPE_ALIASES[canonical] || [canonical]).includes(m.MachineType as string);

  useEffect(() => {
    loadProducts();
    loadMachines();
  }, [showInactive, unitFilter]);

  useEffect(() => {
    loadPhaseTypes();
    loadProductionUnits();
  }, []);

  const loadPhaseTypes = async () => {
    try {
      const data = await mesApi.getProductionPhaseTypes();
      setPhaseTypes(data);
    } catch (err: any) {
      console.error("Failed to load phase types:", err);
    }
  };

  const loadProductionUnits = async () => {
    try {
      setProductionUnits(await mesApi.getProductionUnits());
    } catch (err: any) {
      console.error("Failed to load production units:", err);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await mesApi.getProducts({
        is_active: showInactive ? undefined : true,
        search: searchValue || undefined,
        with_phases: true,
        production_unit: unitFilter ? Number(unitFilter) : undefined,
      });
      setProducts(data);
    } catch (err: any) {
      toast.error("Failed to load products: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMachines = async () => {
    try {
      const data = await mesApi.getMachines();
      setMachines(data);
    } catch (err: any) {
      console.error("Failed to load machines:", err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleEditProduct = async (product: Product) => {
    setSelectedProduct({ ...product });
    setIsCreating(false);
    setEditModalOpen(true);
    // Always load the phases from the API rather than reusing the ones on the list
    // row. The list carries each phase's code and duration only — enough for its
    // columns — so seeding the editor from it would blank out machine, workers and
    // temperature, and saving would then write those blanks back.
    setPhaseRows([]);
    setPhasesLoading(true);
    try {
      setPhaseRows(phasesToRows(await mesApi.getProductPhases(product.ID)));
    } catch (err: any) {
      console.error("Failed to load product phases:", err);
      toast.error(t("Greska pri ucitavanju faza: {message}", { message: err.message }));
    } finally {
      setPhasesLoading(false);
    }
  };

  const handleAddProduct = () => {
    // Pekara je predodabrana jer je to vecina asortimana; slasticarski se prebaci
    // u dropdownu. Ako je filter po pogonu aktivan, novi proizvod ide u taj pogon.
    const defaultUnit = unitFilter
      ? Number(unitFilter)
      : productionUnits.find((u) => u.Code === "pekara")?.ID ?? null;
    setSelectedProduct({ ...emptyProduct, ProductionUnit: defaultUnit });
    setIsCreating(true);
    setEditModalOpen(true);
    setPhaseRows([]);
  };

  const handleSaveProduct = async () => {
    if (!selectedProduct) return;

    if (!selectedProduct.ProductName.trim()) {
      toast.error(t("Naziv proizvoda je obavezan"));
      return;
    }

    setLoading(true);
    try {
      const phasePayload = rowsToPayload(phaseRows);
      if (isCreating) {
        const created = await mesApi.createProduct(selectedProduct);
        if (created?.ID) {
          await mesApi.setProductPhases(created.ID, phasePayload);
        }
        toast.success(t("Proizvod uspjesno dodan"));
      } else {
        await mesApi.updateProduct(selectedProduct.ID, selectedProduct);
        await mesApi.setProductPhases(selectedProduct.ID, phasePayload);
        toast.success(t("Proizvod uspjesno azuriran"));
      }
      // Potvrdi eventualni nespremljeni red dodatnih sirovina (draft u editoru),
      // da se ne izgubi kad korisnik klikne glavni "Spremi".
      await phaseMaterialRef.current?.commitPending();
      setEditModalOpen(false);
      loadProducts();
    } catch (err: any) {
      toast.error(
        t(isCreating ? "Greska pri dodavanju proizvoda: {message}" : "Greska pri azuriranju proizvoda: {message}", {
          message: err.message,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof Product, value: any) => {
    if (!selectedProduct) return;
    setSelectedProduct({
      ...selectedProduct,
      [field]: value,
    });
  };

  // Backend search matches ProductName; also match ProductCode client-side so
  // typing a code finds the product too.
  const displayedProducts = products.filter((p) => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.ProductName || "").toLowerCase().includes(q) ||
      (p.ProductCode || "").toLowerCase().includes(q)
    );
  });

  // ── Bulk selection + machine assignment ──────────────────────────────
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = displayedProducts.length > 0 && displayedProducts.every((p) => selectedIds.has(p.ID));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (displayedProducts.every((p) => prev.has(p.ID))) return new Set();
      return new Set(displayedProducts.map((p) => p.ID));
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkMixer("");
    setBulkProcessing("");
    setBulkOven("");
  };

  // Apply the chosen machines to every selected product. Only fields the user
  // actually set (non-empty) are written, so a bulk edit can target just the
  // mixer without clearing processing/oven. Uses the existing per-product update.
  const applyBulkMachines = async () => {
    if (selectedIds.size === 0) return;
    if (!bulkMixer && !bulkProcessing && !bulkOven) {
      toast.info(t("Odaberite barem jedan stroj za dodjelu."));
      return;
    }
    const patch: Partial<Product> = {};
    if (bulkMixer) patch.MixerMachine = Number(bulkMixer);
    if (bulkProcessing) patch.ProcessingMachine = Number(bulkProcessing);
    if (bulkOven) patch.OvenMachine = Number(bulkOven);

    setBulkSaving(true);
    const ids = Array.from(selectedIds);
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await mesApi.updateProduct(id, patch);
        ok += 1;
      } catch (err) {
        failed += 1;
        console.error(`Bulk update failed for product ${id}:`, err);
      }
    }
    setBulkSaving(false);
    if (failed === 0) toast.success(t("Strojevi dodijeljeni za {ok} proizvoda.", { ok }));
    else toast.warn(t("Ažurirano {ok}, neuspješno {failed}.", { ok, failed }));
    clearSelection();
    loadProducts();
  };

  const headers = [
    { label: t("Kod"), key: "product_code", type: "text" },
    { label: t("Naziv"), key: "product_name", type: "text" },
    { label: t("Kategorija"), key: "category", type: "text" },
    { label: t("Mijesanje (min)"), key: "mixing_time_min", type: "number" },
    { label: t("Fermentacija (min)"), key: "bulk_fermentation_min", type: "number" },
    { label: t("Pecenje (min)"), key: "baking_time", type: "number" },
    { label: t("Masa (kg)"), key: "default_batch_mass_kg", type: "number" },
    { label: t("Aktivan"), key: "is_active", type: "boolean" },
  ];

  return (
    <div className="mes-page">
      {/* No searchFunction: the page already has its own search field, and a
          second one in the header pushed the title out of its usual position. */}
      <Header title={t("MES - Baza proizvoda")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Filters */}
          <div className="mes-section filter-section" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 260 }}>
              <input
                type="text"
                className="input-classic"
                placeholder={t("Pretraži proizvode (naziv ili kod)…")}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ flex: 1, maxWidth: 360 }}
              />
              <select
                className="select-classic"
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
                style={{ maxWidth: 180 }}
              >
                <option value="">{t("Svi pogoni")}</option>
                {productionUnits.map((u) => (
                  <option key={u.ID} value={u.ID}>{u.Name}</option>
                ))}
              </select>
              <label style={{ whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                {t("Prikazi neaktivne proizvode")}
              </label>
            </div>
            <button className="btn-submit" onClick={handleAddProduct}>
              {t("+ Dodaj proizvod")}
            </button>
          </div>

          {/* Bulk edit toolbar — shown when one or more products are selected */}
          {selectedIds.size > 0 && (
            <div
              className="mes-section"
              style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.35)" }}
            >
              <strong style={{ alignSelf: "center" }}>{t("{count} odabrano", { count: selectedIds.size })}</strong>
              <div className="control-group" style={{ marginBottom: 0 }}>
                <label>{t("Mikser")}</label>
                <select className="select-classic" value={bulkMixer} onChange={(e) => setBulkMixer(e.target.value)}>
                  <option value="">—</option>
                  {machines.filter((m) => isMachineType(m, "Mikser")).map((m) => (
                    <option key={m.ID} value={m.ID}>{m.MachineName}</option>
                  ))}
                </select>
              </div>
              <div className="control-group" style={{ marginBottom: 0 }}>
                <label>{t("Obrada")}</label>
                <select className="select-classic" value={bulkProcessing} onChange={(e) => setBulkProcessing(e.target.value)}>
                  <option value="">—</option>
                  {machines.filter((m) => isMachineType(m, "Obrada")).map((m) => (
                    <option key={m.ID} value={m.ID}>{m.MachineName}</option>
                  ))}
                </select>
              </div>
              <div className="control-group" style={{ marginBottom: 0 }}>
                <label>{t("Peć")}</label>
                <select className="select-classic" value={bulkOven} onChange={(e) => setBulkOven(e.target.value)}>
                  <option value="">—</option>
                  {machines.filter((m) => isMachineType(m, "Peć")).map((m) => (
                    <option key={m.ID} value={m.ID}>{m.MachineName}</option>
                  ))}
                </select>
              </div>
              <button className="btn-submit" disabled={bulkSaving} onClick={applyBulkMachines}>
                {bulkSaving ? t("Spremam…") : t("Dodijeli strojeve")}
              </button>
              <button className="btn-edit" disabled={bulkSaving} onClick={clearSelection}>
                {t("Odustani")}
              </button>
            </div>
          )}

          {/* Products Table */}
          <div className="mes-section">
            <div className="mes-table-wrap">
              <table className="mes-table products-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      title={t("Odaberi sve")}
                    />
                  </th>
                  <th>{t("Kod")}</th>
                  <th>{t("Naziv")}</th>
                  <th>{t("Pogon")}</th>
                  <th>{t("Kategorija")}</th>
                  <th>{t("Mikser")}</th>
                  <th>{t("Mijesanje")}</th>
                  <th>{t("Fermentacija")}</th>
                  <th>{t("Pecenje")}</th>
                  <th>{t("Masa")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Akcije")}</th>
                </tr>
              </thead>
              <tbody>
                {displayedProducts.map((product) => (
                  <tr key={product.ID} className={`${!product.IsActive ? "inactive-row" : ""} ${selectedIds.has(product.ID) ? "selected-row" : ""}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(product.ID)}
                        onChange={() => toggleSelect(product.ID)}
                      />
                    </td>
                    <td><code className="product-code">{product.ProductCode || product.ID}</code></td>
                    <td className="product-name">{product.ProductName}</td>
                    <td>
                      {product.ProductionUnit_ProductToProductionUnit?.Name ? (
                        <span className={`unit-badge unit-${product.ProductionUnit_ProductToProductionUnit.Code}`}>
                          {product.ProductionUnit_ProductToProductionUnit.Name}
                        </span>
                      ) : "-"}
                    </td>
                    <td>{product.Category}</td>
                    <td>
                      {product.Machine_MachineToProduct_MixerMachine?.MachineName ? (
                        <span className="mixer-badge">{product.Machine_MachineToProduct_MixerMachine.MachineName}</span>
                      ) : "-"}
                    </td>
                    <td>{phaseDuration(product, "mixing", product.MixingTimeMin)}</td>
                    <td>{phaseDuration(product, "bulk_fermentation", product.BulkFermentationMin)}</td>
                    <td>{phaseDuration(product, "baking", product.BakingTime)}</td>
                    <td>{product.DefaultBatchMassKg} kg</td>
                    <td>
                      <span className={`status-pill status-${product.IsActive ? "active" : "inactive"}`}>
                        <span className="status-dot"></span>
                        {product.IsActive ? t("Aktivan") : t("Neaktivan")}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditProduct(product)}
                      >
                        {t("Uredi")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {displayedProducts.length === 0 && !loading && (
              <div className="empty-state">
                <p>{t("Nema proizvoda")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Product Modal */}
      {editModalOpen && selectedProduct && (
        <div className="mes-modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="mes-modal product-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">📦</div>
                <div className="modal-title-text">
                  <h3>{isCreating ? t("NOVI PROIZVOD") : t("UREDI PROIZVOD")}</h3>
                  <p className="modal-subtitle">{isCreating ? t("Dodavanje novog proizvoda") : selectedProduct.ProductName}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setEditModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>{t("Naziv proizvoda")}</label>
                  <input
                    type="text"
                    placeholder={t("Naziv proizvoda")}
                    value={selectedProduct.ProductName}
                    onChange={(e) => handleFieldChange("ProductName", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t("Sifra")}</label>
                  <input
                    type="text"
                    placeholder={t("Sifra proizvoda")}
                    value={selectedProduct.ProductCode || ""}
                    onChange={(e) => handleFieldChange("ProductCode", e.target.value)}
                  />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>{t("Pogon")}</label>
                  <select
                    value={selectedProduct.ProductionUnit ?? ""}
                    onChange={(e) =>
                      handleFieldChange("ProductionUnit", e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">{t("— nije odreden —")}</option>
                    {productionUnits.map((u) => (
                      <option key={u.ID} value={u.ID}>{u.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("Kategorija")}</label>
                  <input
                    type="text"
                    placeholder={t("Kategorija")}
                    value={selectedProduct.Category || ""}
                    onChange={(e) => handleFieldChange("Category", e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t("Standardna masa zamjesa (kg)")}</label>
                <input
                  type="number"
                  value={String(selectedProduct.DefaultBatchMassKg || "")}
                  onChange={(e) => handleFieldChange("DefaultBatchMassKg", Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <PhaseEditor
                  rows={phaseRows}
                  onChange={setPhaseRows}
                  phaseTypes={phaseTypes}
                  machines={machines}
                />
              </div>
              <div className="form-group">
                <PhaseMaterialEditor
                  ref={phaseMaterialRef}
                  productId={selectedProduct.ID}
                  phaseTypes={phaseTypes}
                />
              </div>
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    checked={selectedProduct.IsActive ?? true}
                    onChange={(e) => handleFieldChange("IsActive", e.target.checked)}
                  />
                  <label>{t("Aktivan proizvod")}</label>
                </div>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    checked={!!selectedProduct.HideInTraceability}
                    onChange={(e) => handleFieldChange("HideInTraceability", e.target.checked)}
                  />
                  <label>{t("Sakrij u sljedivosti")}</label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setEditModalOpen(false)}>
                {t("Odustani")}
              </button>
              {!isCreating && selectedProduct?.ID ? (
                <button
                  className="btn-edit"
                  onClick={() => { window.location.href = `/mes/recipes?product_id=${selectedProduct.ID}`; }}
                  title={t("Otvori recept ovog proizvoda")}
                >
                  {t("Uredi recept")}
                </button>
              ) : null}
              {/* Blocked while the phases load: saving sooner would push the still
                  empty editor rows over the product's real phases. */}
              <button className="btn-submit" onClick={handleSaveProduct} disabled={loading || phasesLoading}>
                {loading ? t("Spremanje...") : phasesLoading ? t("Ucitavanje faza...") : isCreating ? t("Dodaj") : t("Spremi")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default Products;
